import { App } from "@capacitor/app";
import { Capacitor } from "@capacitor/core";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import {
  collectTodayUsage,
  getUsagePermissionState,
  requestUsagePermission,
  type UsagePermissionState,
} from "./usage";

type UsageAccessContextValue = {
  permission: UsagePermissionState | null;
  permissionReady: boolean;
  requiresEntitlement: boolean;
  wasPreviouslyGranted: boolean;
  refreshPermission: () => Promise<UsagePermissionState>;
  requestPermission: () => Promise<UsagePermissionState>;
  collectToday: () => Promise<Awaited<ReturnType<typeof collectTodayUsage>>>;
};

const UsageAccessContext = createContext<UsageAccessContextValue | null>(null);

// iOS silently revokes Screen Time authorization when the user changes
// Screen Time settings (e.g. re-enabling App & Website Activity). Remembering
// that access was granted before lets the UI explain the revocation instead
// of presenting it as a never-requested permission.
const EVER_GRANTED_KEY = "screen0.usagePermissionEverGranted";

function readEverGranted(): boolean {
  try {
    return localStorage.getItem(EVER_GRANTED_KEY) === "1";
  } catch {
    return false;
  }
}

/** True when a prior grant suggests iOS may return a stale read on cold start. */
export function shouldReconcileIosPermissionOnRead(
  state: UsagePermissionState,
  wasPreviouslyGranted: boolean,
  platform: string = Capacitor.getPlatform(),
): boolean {
  return (
    platform === "ios" &&
    wasPreviouslyGranted &&
    state.status !== "granted" &&
    state.requiresEntitlement !== true &&
    state.reason !== "plugin_unregistered"
  );
}

export async function resolveIosPermissionState(options: {
  wasPreviouslyGranted: boolean;
  read: () => Promise<UsagePermissionState>;
  request: () => Promise<UsagePermissionState>;
  platform?: string;
}): Promise<UsagePermissionState> {
  const platform = options.platform ?? Capacitor.getPlatform();
  let state = await options.read();
  if (shouldReconcileIosPermissionOnRead(state, options.wasPreviouslyGranted, platform)) {
    state = await options.request();
  }
  return state;
}

export function UsageAccessProvider({ children }: { children: React.ReactNode }) {
  const [permission, setPermission] = useState<UsagePermissionState | null>(null);
  const [permissionReady, setPermissionReady] = useState(Capacitor.getPlatform() !== "ios");
  const [everGranted, setEverGranted] = useState(readEverGranted);

  const recordGrant = useCallback((state: UsagePermissionState) => {
    if (state.status !== "granted") return;
    try {
      localStorage.setItem(EVER_GRANTED_KEY, "1");
    } catch {
      // Persistence is best-effort; in-memory state still covers this session.
    }
    setEverGranted(true);
  }, []);

  const refreshPermission = useCallback(async (options?: { reconcileIfPreviouslyGranted?: boolean }) => {
    const state =
      options?.reconcileIfPreviouslyGranted === true
        ? await resolveIosPermissionState({
            wasPreviouslyGranted: readEverGranted(),
            read: getUsagePermissionState,
            request: requestUsagePermission,
          })
        : await getUsagePermissionState();
    setPermission(state);
    setPermissionReady(true);
    recordGrant(state);
    return state;
  }, [recordGrant]);

  const requestPermission = useCallback(async () => {
    const state = await requestUsagePermission();
    setPermission(state);
    setPermissionReady(true);
    recordGrant(state);
    return state;
  }, [recordGrant]);

  const collectToday = useCallback(async () => collectTodayUsage(), []);

  useEffect(() => {
    void refreshPermission({ reconcileIfPreviouslyGranted: true });
  }, [refreshPermission]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    const listener = App.addListener("appStateChange", (event) => {
      if (event.isActive) {
        void refreshPermission();
      }
    });

    return () => {
      void listener.then((handle) => handle.remove());
    };
  }, [refreshPermission]);

  const value = useMemo(
    () => ({
      permission,
      permissionReady,
      requiresEntitlement: permission?.requiresEntitlement === true,
      wasPreviouslyGranted: everGranted,
      refreshPermission,
      requestPermission,
      collectToday,
    }),
    [collectToday, everGranted, permission, permissionReady, refreshPermission, requestPermission],
  );

  return <UsageAccessContext.Provider value={value}>{children}</UsageAccessContext.Provider>;
}

export function useUsageAccess() {
  const context = useContext(UsageAccessContext);
  if (!context) {
    throw new Error("useUsageAccess must be used within UsageAccessProvider");
  }
  return context;
}

export function resolveLivePermission(
  permissionReady: boolean,
  live: UsagePermissionState | null,
  snapshotPermission: "granted" | "denied" | "unavailable",
): "granted" | "denied" | "notDetermined" | "unavailable" {
  if (permissionReady && live) {
    return live.status;
  }
  return snapshotPermission;
}

export function isGrantedUsageView(
  permissionReady: boolean,
  live: UsagePermissionState | null,
  resolvedPermission: ReturnType<typeof resolveLivePermission>,
): boolean {
  if (
    permissionReady &&
    live &&
    (live.status === "notDetermined" || live.status === "denied")
  ) {
    return false;
  }
  return resolvedPermission === "granted";
}

/** iOS-only: block main tabs until Screen Time permission is known and granted. */
export function isIosUsagePermissionGateActive(
  permissionReady: boolean,
  permission: UsagePermissionState | null,
  platform: string = Capacitor.getPlatform(),
): boolean {
  if (platform !== "ios") return false;
  if (!permissionReady) return false;
  return permission?.status !== "granted";
}

export function canRequestIosUsagePermission(
  permissionReady: boolean,
  livePermission: UsagePermissionState | null,
  requiresEntitlement: boolean,
  snapshotPermission: "granted" | "denied" | "unavailable" = "unavailable",
  platform: string = Capacitor.getPlatform(),
): boolean {
  if (platform !== "ios" || requiresEntitlement) return false;
  const permission = resolveLivePermission(permissionReady, livePermission, snapshotPermission);
  return (
    permission === "denied" ||
    permission === "notDetermined" ||
    livePermission?.reason === "authorization_status_unknown" ||
    livePermission?.reason === "data_access_not_granted"
  );
}

/**
 * Whether the product should show a CTA that starts the platform permission path.
 * - iOS: native Screen Time request (and related denied / data-access states)
 * - Android: open Usage Access Settings (special AppOps permission — no dialog)
 */
export function canRequestUsagePermission(
  permissionReady: boolean,
  livePermission: UsagePermissionState | null,
  requiresEntitlement: boolean,
  snapshotPermission: "granted" | "denied" | "unavailable" = "unavailable",
  platform: string = Capacitor.getPlatform(),
): boolean {
  if (platform === "ios") {
    return canRequestIosUsagePermission(
      permissionReady,
      livePermission,
      requiresEntitlement,
      snapshotPermission,
      platform,
    );
  }
  if (platform !== "android") return false;
  if (!permissionReady) return false;
  const permission = resolveLivePermission(permissionReady, livePermission, snapshotPermission);
  // Android never has notDetermined; denied/unavailable both mean "open Usage Access".
  return permission === "denied" || permission === "unavailable";
}
